/**
 * Mad Dad Labz Performance Monitor
 * Measures Core Web Vitals and reports performance metrics
 * Target: LCP ≤2.5s, CLS ≤0.1, FID ≤100ms
 */

class MadDadPerformanceMonitor {
  constructor() {
    this.metrics = {
      lcp: null,
      fid: null,
      cls: null,
      ttfb: null,
      fcp: null
    };
    
    this.thresholds = {
      lcp: 2500, // 2.5 seconds
      fid: 100,  // 100 milliseconds
      cls: 0.1,  // 0.1 cumulative layout shift
      ttfb: 800, // 800 milliseconds
      fcp: 1800  // 1.8 seconds
    };
    
    this.init();
  }

  init() {
    // Only monitor in production or when explicitly enabled
    if (this.shouldMonitor()) {
      this.measureCoreWebVitals();
      this.measureCustomMetrics();
      this.setupReporting();
    }
  }

  shouldMonitor() {
    // Check if monitoring is enabled
    const isProduction = !window.Shopify?.designMode;
    const isEnabled = localStorage.getItem('mad-dad-perf-monitor') === 'true';
    const hasConsent = this.hasPerformanceConsent();
    
    return (isProduction || isEnabled) && hasConsent;
  }

  hasPerformanceConsent() {
    // Check for privacy consent - only use localStorage, no external tracking
    return localStorage.getItem('mad-dad-perf-consent') !== 'false';
  }

  /**
   * Measure Core Web Vitals using Web Vitals API
   */
  measureCoreWebVitals() {
    // Largest Contentful Paint (LCP)
    this.measureLCP();
    
    // First Input Delay (FID)
    this.measureFID();
    
    // Cumulative Layout Shift (CLS)
    this.measureCLS();
    
    // Time to First Byte (TTFB)
    this.measureTTFB();
    
    // First Contentful Paint (FCP)
    this.measureFCP();
  }

  measureLCP() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        this.metrics.lcp = Math.round(lastEntry.startTime);
        this.reportMetric('lcp', this.metrics.lcp);
      });
      
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    }
  }

  measureFID() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          this.metrics.fid = Math.round(entry.processingStart - entry.startTime);
          this.reportMetric('fid', this.metrics.fid);
        });
      });
      
      observer.observe({ entryTypes: ['first-input'] });
    }
  }

  measureCLS() {
    if ('PerformanceObserver' in window) {
      let clsValue = 0;
      let sessionValue = 0;
      let sessionEntries = [];
      
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        
        entries.forEach(entry => {
          if (!entry.hadRecentInput) {
            const firstSessionEntry = sessionEntries[0];
            const lastSessionEntry = sessionEntries[sessionEntries.length - 1];
            
            if (sessionValue && 
                entry.startTime - lastSessionEntry.startTime < 1000 &&
                entry.startTime - firstSessionEntry.startTime < 5000) {
              sessionValue += entry.value;
              sessionEntries.push(entry);
            } else {
              sessionValue = entry.value;
              sessionEntries = [entry];
            }
            
            if (sessionValue > clsValue) {
              clsValue = sessionValue;
              this.metrics.cls = Math.round(clsValue * 1000) / 1000;
              this.reportMetric('cls', this.metrics.cls);
            }
          }
        });
      });
      
      observer.observe({ entryTypes: ['layout-shift'] });
    }
  }

  measureTTFB() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (entry.name === window.location.href) {
            this.metrics.ttfb = Math.round(entry.responseStart - entry.requestStart);
            this.reportMetric('ttfb', this.metrics.ttfb);
          }
        });
      });
      
      observer.observe({ entryTypes: ['navigation'] });
    }
  }

  measureFCP() {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          this.metrics.fcp = Math.round(entry.startTime);
          this.reportMetric('fcp', this.metrics.fcp);
        });
      });
      
      observer.observe({ entryTypes: ['paint'] });
    }
  }

  /**
   * Measure custom Mad Dad Labz specific metrics
   */
  measureCustomMetrics() {
    // Measure hero section load time
    this.measureHeroLoadTime();
    
    // Measure chaos grid render time
    this.measureChaosGridTime();
    
    // Measure Easter egg system load time
    this.measureEasterEggLoadTime();
    
    // Measure font load times
    this.measureFontLoadTimes();
  }

  measureHeroLoadTime() {
    const heroElement = document.querySelector('.mad-dad-hero');
    if (heroElement) {
      const startTime = performance.now();
      
      const checkHeroLoaded = () => {
        const heroTitle = heroElement.querySelector('.mad-dad-hero-title');
        const heroImage = heroElement.querySelector('img');
        
        if (heroTitle && (!heroImage || heroImage.complete)) {
          const loadTime = Math.round(performance.now() - startTime);
          this.reportCustomMetric('hero-load-time', loadTime);
        } else {
          requestAnimationFrame(checkHeroLoaded);
        }
      };
      
      requestAnimationFrame(checkHeroLoaded);
    }
  }

  measureChaosGridTime() {
    const chaosGrid = document.querySelector('.mad-dad-chaos-grid');
    if (chaosGrid) {
      const observer = new MutationObserver(() => {
        const visibleCards = chaosGrid.querySelectorAll('.card-wrapper:not(.mad-dad-loading)');
        if (visibleCards.length > 0) {
          const renderTime = performance.now();
          this.reportCustomMetric('chaos-grid-render', Math.round(renderTime));
          observer.disconnect();
        }
      });
      
      observer.observe(chaosGrid, { childList: true, subtree: true, attributes: true });
    }
  }

  measureEasterEggLoadTime() {
    if (window.madDadEasterEggs) {
      const loadTime = performance.now();
      this.reportCustomMetric('easter-egg-load', Math.round(loadTime));
    }
  }

  measureFontLoadTimes() {
    if ('fonts' in document) {
      const fonts = ['Bungee', 'Baloo 2', 'Inter'];
      
      fonts.forEach(fontFamily => {
        document.fonts.load(`16px "${fontFamily}"`).then(() => {
          const loadTime = performance.now();
          this.reportCustomMetric(`font-${fontFamily.toLowerCase().replace(' ', '-')}-load`, Math.round(loadTime));
        });
      });
    }
  }

  /**
   * Report metric with threshold checking
   */
  reportMetric(metric, value) {
    const threshold = this.thresholds[metric];
    const status = threshold ? (value <= threshold ? 'good' : 'poor') : 'info';
    
    // Store in localStorage for debugging
    const perfData = JSON.parse(localStorage.getItem('mad-dad-perf-data') || '{}');
    perfData[metric] = { value, status, timestamp: Date.now() };
    localStorage.setItem('mad-dad-perf-data', JSON.stringify(perfData));
    
    // Console logging for development
    if (window.Shopify?.designMode || localStorage.getItem('mad-dad-debug') === 'true') {
      console.log(`Mad Dad Labz Performance - ${metric.toUpperCase()}: ${value}ms (${status})`);
    }
    
    // Report to analytics if configured (privacy-safe)
    this.sendToAnalytics(metric, value, status);
  }

  reportCustomMetric(metric, value) {
    // Store custom metrics
    const perfData = JSON.parse(localStorage.getItem('mad-dad-perf-data') || '{}');
    perfData[metric] = { value, timestamp: Date.now() };
    localStorage.setItem('mad-dad-perf-data', JSON.stringify(perfData));
    
    if (window.Shopify?.designMode || localStorage.getItem('mad-dad-debug') === 'true') {
      console.log(`Mad Dad Labz Custom Metric - ${metric}: ${value}ms`);
    }
  }

  /**
   * Send metrics to analytics (privacy-safe, localStorage only)
   */
  sendToAnalytics(metric, value, status) {
    // Only use localStorage - no external tracking
    const analyticsData = JSON.parse(localStorage.getItem('mad-dad-analytics') || '{}');
    
    if (!analyticsData.sessions) {
      analyticsData.sessions = [];
    }
    
    const currentSession = analyticsData.sessions[analyticsData.sessions.length - 1];
    const sessionId = this.getSessionId();
    
    if (!currentSession || currentSession.id !== sessionId) {
      analyticsData.sessions.push({
        id: sessionId,
        timestamp: Date.now(),
        url: window.location.pathname,
        metrics: {}
      });
    }
    
    const session = analyticsData.sessions[analyticsData.sessions.length - 1];
    session.metrics[metric] = { value, status };
    
    // Keep only last 10 sessions to prevent localStorage bloat
    if (analyticsData.sessions.length > 10) {
      analyticsData.sessions = analyticsData.sessions.slice(-10);
    }
    
    localStorage.setItem('mad-dad-analytics', JSON.stringify(analyticsData));
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('mad-dad-session-id');
    if (!sessionId) {
      sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
      sessionStorage.setItem('mad-dad-session-id', sessionId);
    }
    return sessionId;
  }

  /**
   * Setup performance reporting and alerts
   */
  setupReporting() {
    // Report summary after page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.generatePerformanceReport();
      }, 3000);
    });
    
    // Report on page visibility change (user leaving)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.generatePerformanceReport();
      }
    });
  }

  generatePerformanceReport() {
    const report = {
      timestamp: Date.now(),
      url: window.location.pathname,
      metrics: this.metrics,
      thresholds: this.thresholds,
      userAgent: navigator.userAgent,
      connection: this.getConnectionInfo()
    };
    
    // Store report
    const reports = JSON.parse(localStorage.getItem('mad-dad-perf-reports') || '[]');
    reports.push(report);
    
    // Keep only last 5 reports
    if (reports.length > 5) {
      reports.splice(0, reports.length - 5);
    }
    
    localStorage.setItem('mad-dad-perf-reports', JSON.stringify(reports));
    
    // Log summary in development
    if (window.Shopify?.designMode || localStorage.getItem('mad-dad-debug') === 'true') {
      console.table(this.metrics);
    }
  }

  getConnectionInfo() {
    if ('connection' in navigator) {
      return {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt
      };
    }
    return null;
  }

  /**
   * Public API for manual performance checks
   */
  getMetrics() {
    return { ...this.metrics };
  }

  getReport() {
    const reports = JSON.parse(localStorage.getItem('mad-dad-perf-reports') || '[]');
    return reports[reports.length - 1] || null;
  }

  clearData() {
    localStorage.removeItem('mad-dad-perf-data');
    localStorage.removeItem('mad-dad-analytics');
    localStorage.removeItem('mad-dad-perf-reports');
    console.log('Mad Dad Labz performance data cleared');
  }
}

// Initialize performance monitor
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.madDadPerformanceMonitor = new MadDadPerformanceMonitor();
  });
} else {
  window.madDadPerformanceMonitor = new MadDadPerformanceMonitor();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MadDadPerformanceMonitor;
}